import React, { useEffect } from 'react';
import { CreditCardFormState, CreditCardState } from './states/CreditCardFormState';
import './cardInput.module.scss';
import { BrandIcon } from '../../../../mainv2/containers/common/payment/CardSummaryView';
import { Button } from '../../../../commonv3/components/atoms/buttons/Button';
import { observer } from 'mobx-react';
import { FormError } from '../../../../commonv3/components/atoms/forms/vertical';
import { StringInput } from '../../../../commonv3/components/atoms/forms/primitive/StringInput';
import type { CardSummary as CardSummaryType } from '../../../../mainv2/types/card';

type FormProps = {
  formState: CreditCardFormState;
  resettable?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onResetRequest?: () => any;
};

export const CardInput = observer(
  class CardInput extends React.Component<FormProps> {
    formatCcNumber(value) {
      const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
      const matches = v.match(/\d{4,16}/g);
      const match = (matches && matches[0]) || '';
      const parts = [];
      let i, len;
      for (i = 0, len = match.length; i < len; i += 4) {
        parts.push(match.substring(i, i + 4));
      }
      if (parts.length) {
        return parts.join(' ');
      } else {
        return value;
      }
    }
    handleChange(value, state) {
      const cc_number = value.replace(/\s+/g, '');
      state.setDirtyValue(cc_number);
    }
    render() {
      const { formState, resettable = false, onResetRequest = undefined } = this.props;
      const { number, yy, mm, cvc } = formState.fields;
      return (
        <div styleName="form">
          <div>
            <BrandIcon styleName="brand-icon" brand="visa" />
            <BrandIcon styleName="brand-icon" brand="master" />
            <BrandIcon styleName="brand-icon" brand="american" />
            <BrandIcon styleName="brand-icon" brand="diners" />
            <BrandIcon styleName="brand-icon" brand="jcb" />
          </div>
          <div styleName="form-row">
            <div>
              <div styleName="label">カード番号</div>
              <StringInput
                id="card_number"
                styleName="input"
                value={this.formatCcNumber(number.getDirtyValue())}
                error={number.hasErrors()}
                type="text"
                placeholder="例：1234 1234 1234 1234"
                onChange={(e) => this.handleChange(this.formatCcNumber(e), number)}
                onBlur={number.validate}
              />
              <FormError state={number} />
            </div>
          </div>
          <div styleName="form-row">
            <div styleName="label">有効期限</div>
            <div styleName="form-row-body">
              <div styleName="input-wrapper">
                <StringInput
                  id="expiry_month"
                  styleName="input"
                  value={mm.getDirtyValue()}
                  error={mm.hasErrors()}
                  type="text"
                  placeholder="月（MM）"
                  onChange={mm.setDirtyValue}
                  onBlur={mm.validate}
                />
              </div>
              <div styleName="input-wrapper">
                <StringInput
                  id="expiry_year"
                  styleName="input"
                  value={yy.getDirtyValue()}
                  error={yy.hasErrors()}
                  type="text"
                  placeholder="年（YY）"
                  onChange={yy.setDirtyValue}
                  onBlur={yy.validate}
                />
              </div>
            </div>
          </div>
          <div styleName="form-row">
            <div styleName="form-row-body">
              <div styleName="input-wrapper">
                <FormError state={mm} />
              </div>
              <div styleName="input-wrapper">
                <FormError state={yy} />
              </div>
            </div>
          </div>
          <div styleName="form-row">
            <div styleName="label">セキュリティコード</div>
            <div styleName="form-row-body">
              <div styleName="input-wrapper security">
                <StringInput
                  id="cvc"
                  styleName="input"
                  value={cvc.getDirtyValue()}
                  error={cvc.hasErrors()}
                  type="text"
                  placeholder="例：123"
                  onChange={cvc.setDirtyValue}
                  onBlur={cvc.validate}
                />
              </div>
              <FormError state={cvc} />
              {resettable && (
                <Button styleName="button" type="normal" size="s" onClick={onResetRequest}>
                  戻る
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }
  }
);

type CardSummaryProps = {
  card: CardSummaryType;
  modifiable?: boolean;
  onModifyRequest?: () => void;
  className?: string;
};

export const CardSummary = ({
  card,
  modifiable = false,
  onModifyRequest = () => null,
  className = undefined,
}: CardSummaryProps) => (
  <div styleName="card-summary" className={className}>
    <div styleName="summary-main">
      <BrandIcon styleName="brand-icon" brand={card.brand} />
      <div styleName="number-mask">
        {'●'.repeat(4)} {'●'.repeat(4)} {card.last4}
      </div>
    </div>
    {modifiable && (
      <Button type="normal" size="s" onClick={onModifyRequest}>
        変更する
      </Button>
    )}
  </div>
);

type Props = {
  cardState: CreditCardState;
};
export const CreditCardInput = observer(
  class CreditCardInput extends React.Component<Props> {
    render() {
      const {
        cardState: { editMode, cardFormState, card, setEditMode },
      } = this.props;
      if (editMode) {
        return <CardInput formState={cardFormState} resettable={!!card} onResetRequest={() => setEditMode(false)} />;
      }
      if (!card) {
        return null;
      }
      return <CardSummary card={card} modifiable={true} onModifyRequest={() => setEditMode(true)} />;
    }
  }
);

type CardInputV2Props = {
  creditCardState: CreditCardState;
};

export const CardInputV2 = observer(({ creditCardState }: CardInputV2Props) => {
  useEffect(() => {
    creditCardState.setCardInputErrorMessage({ number: '', expiry: '' });
    creditCardState.setCardInputComplete({ number: false, expiry: false, cvc: false });
    const elements = payjp.elements();
    const numberElement = elements.create('cardNumber', { placeholder: '例：1234 1234 1234 1234' });
    const expiryElement = elements.create('cardExpiry', { placeholder: '月（MM）/ 年（YY）' });
    const cvcElement = elements.create('cardCvc', { placeholder: '例：123' });
    numberElement.mount('#number-form');
    expiryElement.mount('#expiry-form');
    cvcElement.mount('#cvc-form');
    numberElement.on('change', (e) => {
      creditCardState.setCardInputErrorMessage({ number: e.error?.message || '' });
      creditCardState.setCardInputComplete({ number: e.complete });
    });
    expiryElement.on('change', (e) => {
      creditCardState.setCardInputErrorMessage({ expiry: e.error?.message || '' });
      creditCardState.setCardInputComplete({ expiry: e.complete });
    });
    cvcElement.on('change', (e) => {
      creditCardState.setCardInputComplete({ cvc: e.complete });
    });
    creditCardState.setCardElement(numberElement);
  }, []);
  return (
    <div styleName="form">
      <div>
        {['visa', 'master', 'american', 'diners', 'jcb'].map((b) => (
          <BrandIcon key={b} styleName="brand-icon" brand={b} />
        ))}
      </div>
      <div styleName="form-row">
        <div>
          <div styleName="label">カード番号</div>
          <div id="number-form" styleName="payjs-outer"></div>
          <div styleName="form-error">{creditCardState.cardInputErrorMessage.number}</div>
        </div>
      </div>
      <div styleName="form-row">
        <div styleName="label">有効期限</div>
        <div styleName="form-row-body">
          <div styleName="input-wrapper">
            <div id="expiry-form" styleName="payjs-outer"></div>
            <div styleName="form-error">{creditCardState.cardInputErrorMessage.expiry}</div>
          </div>
        </div>
      </div>
      <div styleName="form-row">
        <div styleName="label">セキュリティコード</div>
        <div styleName="form-row-body">
          <div styleName="input-wrapper security">
            <div id="cvc-form" styleName="payjs-outer"></div>
          </div>
        </div>
      </div>
    </div>
  );
});

export const CreditCardInputV2 = observer(
  class CreditCardInputV2 extends React.Component<Props> {
    render() {
      const {
        cardState: { editMode, card, setEditMode },
      } = this.props;
      if (editMode) {
        return <CardInputV2 creditCardState={this.props.cardState} />;
      }
      if (!card) {
        return null;
      }
      return <CardSummary card={card} modifiable={true} onModifyRequest={() => setEditMode(true)} />;
    }
  }
);
